import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Grid, Label } from 'components/HeroSemantic';
import ThemeLinkButton from 'components/Theme/ThemeLinkButton';

const formatTime = seconds => {
  // eg 1:30 (90), 0:30 (30)
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  // return formatted string to include leading 0 for seconds and minutes
  return `${minutes}:${remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds}`;
};

const TwoFactorAuthForm = ({ isAdmin, twoFactorDeliveryMethod, twoFaRequestExpiry, onSubmit, onResend2FACode }) => {
  const { formatMessage } = useIntl();
  const subtitleId = isAdmin ? 'page.user.twoFactorAuth.subtitle.admin' : 'page.user.twoFactorAuth.subtitle.user';
  const [input2FaCode, setInput2FaCode] = useState(null);
  const [userRetries, setUserRetries] = useState(0);
  const [request2FACountdownRemaining, setRequest2FACountdownRemaining] = useState(0);

  useEffect(() => {
    if (!twoFaRequestExpiry) {
      return undefined;
    }
    // OWN-231: recompute from the fixed expiry timestamp on every tick,
    // rather than decrementing a counter - a decrementing counter drifts
    // once the browser throttles/pauses setInterval in a backgrounded tab,
    // so returning to the tab showed more time remaining than had actually
    // elapsed against the server-issued expiry. Also resync immediately on
    // visibilitychange, since a throttled tab's next 1s tick can otherwise
    // be delayed well past the moment the tab becomes visible again.
    const syncCountdown = () => {
      const remaining = Math.floor((new Date(twoFaRequestExpiry) - new Date()) / 1000);
      setRequest2FACountdownRemaining(Math.max(remaining, 0));
    };
    syncCountdown();
    const interval = setInterval(syncCountdown, 1000);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncCountdown();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [twoFaRequestExpiry]);

  const handleInputChange = value => {
    setInput2FaCode(value);
  };

  const handleFormSubmit = () => {
    onSubmit(input2FaCode);
  };

  const handleResend2FACode = () => {
    setUserRetries(userRetries + 1);
    onResend2FACode();
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <Grid>
        <Grid.Row>
          <FormattedMessage id={subtitleId} />
        </Grid.Row>
        <Grid.Row>
          <Form.Input
            required
            id="twoFaCode"
            label={formatMessage({ id: 'page.user.twoFactorAuth.form.label.twoFaCode' })}
            style={{
              width: '100px',
            }}
            onChange={(e, { value }) => handleInputChange(value)}
          />
        </Grid.Row>
        <Grid.Row style={{ padding: 0 }} centered>
          <ThemeLinkButton
            label={
              formatMessage(
                request2FACountdownRemaining <= 0 ?
                  { id: 'page.user.twoFactorAuth.retry' }
                  : { id: 'page.user.twoFactorAuth.pending' },
                { value: formatTime(request2FACountdownRemaining) }
              )
            }
            onClick={handleResend2FACode}
            disabled={request2FACountdownRemaining > 0}
          />
        </Grid.Row>
        <Grid.Row centered>
          {userRetries > 0 && (
            <Label pointing="below" prompt>
              <FormattedMessage id={`page.user.twoFactorAuth.${twoFactorDeliveryMethod === 'sms' ? 'suggestionTryVoice' : 'suggestionTrySMS'}`} />
            </Label>
          )}
          <Button type="submit" color="green" size="large" disabled={!input2FaCode}>
            <FormattedMessage id="general.submit" />
          </Button>
        </Grid.Row>
      </Grid>
    </Form>
  );
};

TwoFactorAuthForm.propTypes = {
  isAdmin: PropTypes.bool,
  twoFactorDeliveryMethod: PropTypes.string.isRequired,
  twoFaRequestExpiry: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onResend2FACode: PropTypes.func.isRequired,
};

TwoFactorAuthForm.defaultProps = {
  isAdmin: false,
};

export default TwoFactorAuthForm;
